import {DocumentDuplicateIcon} from '@heroicons/react/solid';
import React, {Fragment} from 'react';

import {Badge} from '@/components';
import type {BusinessPaymentMethod} from '@/types/AfriexBusinessPaymentMethod';
import type {AfriexBankAccount} from '@/types/AfriexPaymentMethod';
import {capitalizeWords} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';
import {
  formatProvider,
  getAccountType,
  statusColorScheme,
} from '@/utils/merchant';
import {copyToClipboard} from '@/utils/user';

type DetailField = {
  label: string;
  value?: string | number | null;
  copyable?: boolean;
};

const DetailRow = ({
  label,
  value,
  copyable,
}: DetailField): React.JSX.Element => (
  <div className="flex justify-between py-2 border-b dark:border-dark-borderMid last:border-0">
    <p className="text-slate-500 text-sm dark:text-slate-100">{label}</p>
    <div className="w-1/2 flex items-center gap-2">
      <p className="text-sm font-medium text-gray-700 whitespace-normal overflow-hidden dark:text-gray-100 text-right">
        {value}
      </p>
      {copyable && value ? (
        <span
          title={`Copy ${label}`}
          onClick={() => copyToClipboard(String(value), `${label} copied`)}
        >
          <DocumentDuplicateIcon className="w-4 h-4 text-cyan-700 cursor-pointer shrink-0" />
        </span>
      ) : null}
    </div>
  </div>
);

const VirtualAccountSummary = ({
  account,
}: {
  account: BusinessPaymentMethod;
}): React.JSX.Element => {
  const info = (account.info ?? {}) as AfriexBankAccount;

  // Only render fields that carry a value, so provider-specific extras (routing
  // number, IBAN, sort code, SWIFT/BIC) appear only when the rail supplies them.
  const fields: DetailField[] = [
    {label: 'Account Name', value: info.accountName},
    {label: 'Account Number', value: info.accountNumber, copyable: true},
    {label: 'Bank', value: info.bankName},
    {label: 'Bank Code', value: info.bankCode},
    {label: 'Routing Number', value: info.routingNumber},
    {label: 'Sort Code', value: info.sortCode},
    {label: 'SWIFT Code', value: info.swiftCode},
    {label: 'IBAN', value: info.iban},
    {label: 'BIC', value: info.bic},
    {label: 'Branch', value: info.branch},
    {label: 'Currency', value: account.currencyCode ?? info.currency},
    {label: 'Country / Corridor', value: account.countryCode ?? info.country},
    {label: 'Provider', value: formatProvider(account.processor)},
    {label: 'Account Type', value: getAccountType(info)},
    {label: 'Reference', value: info.reference},
    {
      label: 'Expires In (minutes)',
      value: info.expiresInMinutes,
    },
    {
      label: 'Date Created',
      value: account.createdAt
        ? formatDate.DDMonYYYY(account.createdAt.toString())
        : undefined,
    },
    {
      label: 'Last Updated',
      value: account.updatedAt
        ? formatDate.DDMonYYYY(account.updatedAt.toString())
        : undefined,
    },
  ];

  return (
    <div className="whitespace-normal break-words bg-white dark:bg-gray-900 rounded-lg w-full px-5 sm:px-10 py-5 sm:py-8">
      <div className="w-full flex justify-between items-center font-semibold mb-2">
        <div className="flex items-center gap-3">
          <h2 className="text-lg">{info.accountName || 'Virtual Account'}</h2>
          <Badge
            label={capitalizeWords(account.status)}
            colorScheme={statusColorScheme(account.status)}
          />
        </div>
        <div className="text-cyan-700 uppercase text-sm">
          {formatProvider(account.processor)}
        </div>
      </div>
      <hr className="border-1 border-gray-200 dark:border-dark-borderMid mb-4 w-full" />

      <div className="w-full">
        {fields.map((field, idx) =>
          field.value === undefined ||
          field.value === null ||
          field.value === '' ? null : (
            <Fragment key={`${field.label}-${idx}`}>
              <DetailRow {...field} />
            </Fragment>
          ),
        )}
      </div>
    </div>
  );
};

export default VirtualAccountSummary;
